// src/pages/super-admin/SupportManagement.js
import React, { useEffect, useState } from 'react';
import api from '../../api';
import Swal from 'sweetalert2';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaHeadset,
  FaEnvelope,
  FaPhone,
  FaCheck,
  FaTimes,
  FaSave,
  FaUserCheck,
} from 'react-icons/fa';

const EMPTY_FORM = { username: '', email: '', phoneNumber: '', password: '' };

const SupportManagement = () => {
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/support-users');
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching support users:', error);
      Swal.fire('Error', error?.response?.data?.message || 'Failed to fetch support users', 'error');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    if (!form.username?.trim()) return 'Username is required';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) return 'Invalid email format';
    if (!isEditing && (!form.password || form.password.length < 6)) {
      return 'Password must be at least 6 characters';
    }
    if (isEditing && form.password && form.password.length < 6) {
      return 'Password must be at least 6 characters';
    }
    return null;
  };

  const handleSubmit = async () => {
    const error = validateForm();
    if (error) return Swal.fire('Warning', error, 'warning');

    const payload = {
      username: form.username.trim(),
      email: form.email.trim(),
      phoneNumber: form.phoneNumber,
    };
    if (form.password) payload.password = form.password;

    try {
      if (isEditing) {
        await api.put(`/support-users/${editingId}`, payload);
        Swal.fire('Success', 'Support user updated', 'success');
      } else {
        await api.post('/support-users', payload);
        Swal.fire('Success', 'Support user created', 'success');
      }
      fetchUsers();
      closeFormModal();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to save support user', 'error');
    }
  };

  const startEditing = (user) => {
    setIsEditing(true);
    setEditingId(user._id);
    setForm({
      username: user.username || '',
      email: user.email || '',
      phoneNumber: user.phoneNumber || '',
      password: '',
    });
    setShowFormModal(true);
  };

  const handleDeactivate = async (id) => {
    const confirm = await Swal.fire({
      title: 'Deactivate support user?',
      text: 'They will no longer be able to sign in to the support tracker.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, deactivate',
    });
    if (!confirm.isConfirmed) return;
    try {
      await api.put(`/support-users/deactivate/${id}`);
      Swal.fire('Deactivated', 'Support user deactivated', 'success');
      fetchUsers();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to deactivate', 'error');
    }
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Delete permanently?',
      text: 'This cannot be undone.',
      icon: 'error',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
    });
    if (!confirm.isConfirmed) return;
    try {
      await api.delete(`/support-users/${id}`);
      Swal.fire('Deleted', 'Support user deleted', 'success');
      fetchUsers();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setIsEditing(false);
    setEditingId(null);
  };

  const closeFormModal = () => {
    setShowFormModal(false);
    resetForm();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="bg-white rounded-2xl shadow-lg p-8 mb-8 border border-gray-200">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
              <FaHeadset className="text-blue-600 mr-4 text-2xl" />
              Support Team
            </h1>
            <p className="text-gray-600 mt-2 text-sm">
              Create and manage the users who operate the support tracker (support.sanibiz.in)
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowFormModal(true);
            }}
            className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 rounded-xl hover:from-blue-700 hover:to-purple-700 flex items-center space-x-2 shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 text-sm"
          >
            <FaPlus className="w-5 h-5" />
            <span className="font-semibold">Add Support User</span>
          </button>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gradient-to-r from-gray-50 to-gray-100">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Support User
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-100">
              {users.map((user) => (
                <tr key={user._id} className="hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-200 group">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-3">
                      <div className="bg-gradient-to-r from-blue-500 to-purple-500 text-white w-10 h-10 rounded-xl flex items-center justify-center font-bold text-sm">
                        {user.username?.charAt(0)?.toUpperCase()}
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">
                          {user.username}
                        </div>
                        <div className="text-xs text-gray-500">{user.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-xs">
                        <FaEnvelope className="w-3 h-3 text-gray-400" />
                        <span className="text-gray-900">{user.email}</span>
                      </div>
                      {user.phoneNumber && (
                        <div className="flex items-center space-x-2 text-xs">
                          <FaPhone className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-600">{user.phoneNumber}</span>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex items-center space-x-1 text-xs font-bold rounded-full shadow-md ${
                      user.isActive
                        ? 'bg-gradient-to-r from-green-100 to-green-200 text-green-800'
                        : 'bg-gradient-to-r from-red-100 to-red-200 text-red-800'
                    }`}>
                      {user.isActive ? <FaCheck className="w-2 h-2" /> : <FaTimes className="w-2 h-2" />}
                      <span>{user.isActive ? 'Active' : 'Inactive'}</span>
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-xs font-medium">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => startEditing(user)}
                        className="bg-green-100 text-green-600 hover:bg-green-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                        title="Edit"
                      >
                        <FaEdit className="w-3 h-3" />
                      </button>
                      {user.isActive && (
                        <button
                          onClick={() => handleDeactivate(user._id)}
                          className="bg-amber-100 text-amber-600 hover:bg-amber-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                          title="Deactivate"
                        >
                          <FaUserCheck className="w-3 h-3" />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(user._id)}
                        className="bg-red-100 text-red-600 hover:bg-red-200 p-1 rounded-lg transition-all duration-200 hover:scale-110"
                        title="Delete"
                      >
                        <FaTrash className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td colSpan="4" className="px-8 py-16 text-center">
                    <div className="flex flex-col items-center">
                      <FaHeadset className="w-16 h-16 text-gray-300 mb-4" />
                      <span className="text-gray-500 text-lg font-medium">No support users yet</span>
                      <span className="text-gray-400 text-sm mt-2">Add your first support agent to start triaging tickets</span>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showFormModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
          <div className="bg-white w-full max-w-2xl mx-4 rounded-2xl shadow-2xl p-5 relative border border-gray-200 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent flex items-center">
                  <FaHeadset className="text-blue-600 mr-2" />
                  {isEditing ? 'Edit Support User' : 'Add Support User'}
                </h3>
                <p className="text-gray-600 mt-1 text-xs">
                  {isEditing ? 'Update details or set a new password' : 'Create an account to operate the support tracker'}
                </p>
              </div>
              <button
                onClick={closeFormModal}
                className="text-gray-400 hover:text-gray-600 bg-gray-100 hover:bg-gray-200 p-2 rounded-full transition-all"
              >
                <FaTimes className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Username</label>
                <input
                  type="text"
                  name="username"
                  value={form.username}
                  onChange={handleInputChange}
                  placeholder="e.g. Support Agent 1"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white"
                />
              </div>
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleInputChange}
                  placeholder="agent@sanibiz.in"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white"
                />
              </div>
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">Phone Number</label>
                <input
                  type="text"
                  name="phoneNumber"
                  value={form.phoneNumber}
                  onChange={handleInputChange}
                  placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white"
                />
              </div>
              <div className="bg-gray-50 p-2 rounded-xl">
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Password {isEditing && <span className="text-gray-400 font-normal">(leave blank to keep)</span>}
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleInputChange}
                  placeholder={isEditing ? 'New password (optional)' : 'Min 6 characters'}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs bg-white"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-3 border-t border-gray-200 mt-4">
              <button
                onClick={closeFormModal}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold text-sm transition-all flex items-center space-x-1"
              >
                <FaTimes className="w-3 h-3" />
                <span>Cancel</span>
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 flex items-center space-x-1 font-semibold text-sm shadow-lg transition-all"
              >
                <FaSave className="w-3 h-3" />
                <span>{isEditing ? 'Update' : 'Create'}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportManagement;
